<script module>
  import AppLayout from '../../layouts/AppLayout.svelte'
  export const layout = AppLayout
</script>

<script>
  import { router } from '@inertiajs/svelte'
  import { Button } from '$lib/components/ui/button/index.js'
  import PageContainer from '$lib/components/page-container.svelte'
  import { Input } from '$lib/components/ui/input/index.js'
  import { Label } from '$lib/components/ui/label/index.js'
  import { Progress } from '$lib/components/ui/progress/index.js'
  import { Separator } from '$lib/components/ui/separator/index.js'
  import { Alert, AlertTitle, AlertDescription } from '$lib/components/ui/alert/index.js'
  import { Card, CardHeader, CardTitle, CardContent, CardAction } from '$lib/components/ui/card/index.js'
  import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter, SheetDescription
  } from '$lib/components/ui/sheet/index.js'
  import CheckCircleIcon from '@lucide/svelte/icons/check-circle'
  import PencilIcon from '@lucide/svelte/icons/pencil'
  import InfoIcon from '@lucide/svelte/icons/info'
  import XIcon from '@lucide/svelte/icons/x'

  let { property, errors = {} } = $props()

  // ── Mobile sheet state ────────────────────────────────────────────────
  let editSheetOpen = $state(false)
  let interestRm = $state('')
  let submitting = $state(false)

  // ── Desktop inline state ──────────────────────────────────────────────
  let localInterestRm = $state((property.loan_interest_cents / 100).toFixed(2))
  let savingInterest = $state(false)

  function formatRM(cents) {
    return (cents / 100).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  // Mobile sheet handlers
  function openEditSheet() {
    interestRm = (property.loan_interest_cents / 100).toFixed(2)
    editSheetOpen = true
  }

  function handleSave() {
    if (submitting) return
    submitting = true
    const cents = Math.round(parseFloat(interestRm || '0') * 100)
    router.put(`/properties/${property.id}/property_finance`, {
      property_finance: { loan_interest_cents: cents }
    }, {
      preserveState: false,
      onSuccess: () => { editSheetOpen = false },
      onFinish: () => { submitting = false }
    })
  }

  // Desktop blur-to-save handler
  function handleInterestBlur() {
    const newCents = Math.round(parseFloat(localInterestRm || '0') * 100)
    if (newCents === property.loan_interest_cents) return
    savingInterest = true
    router.put(`/properties/${property.id}/property_finance`, {
      property_finance: { loan_interest_cents: newCents }
    }, {
      preserveState: true,
      preserveScroll: true,
      viewTransition: false,
      onFinish: () => { savingInterest = false }
    })
  }
</script>

<svelte:head>
  <title>Property Finance — MyRentalTax</title>
</svelte:head>

<PageContainer>
  {#snippet breadcrumb()}
    <span class="text-[11px] font-medium text-muted-foreground">Property Finance · Step 2 of 5</span>
  {/snippet}
    <!-- Header -->
    <div class="flex flex-col gap-1">
      <h1 class="text-xl sm:text-2xl font-semibold text-foreground m-0">Property Finance</h1>
      <p class="text-sm text-muted-foreground m-0 leading-relaxed">Upload your bank loan statement to extract the interest portion. Only interest payments are tax-deductible.</p>
    </div>

    <!-- Progress row (desktop) -->
    <div class="hidden sm:flex items-center gap-3">
      <span class="text-xs font-medium text-muted-foreground">Step 2 of 5</span>
      <Progress value={2} max={5} class="flex-1" />
    </div>

    <!-- Loan interest card -->
    <Card class="shadow-none border-border">
      <CardHeader class="px-4 sm:px-6 py-3 sm:py-4">
        <div class="flex items-center gap-2">
          {#if property.loan_interest_cents > 0}
            <CheckCircleIcon class="w-4 h-4 text-green-600 flex-shrink-0" />
            <CardTitle class="text-sm font-semibold">Loan Interest Summary</CardTitle>
          {:else}
            <CardTitle class="text-sm font-semibold">Loan Interest</CardTitle>
          {/if}
        </div>
        <!-- Pencil edit button — mobile only -->
        <CardAction class="sm:hidden">
          <Button variant="ghost" size="icon" class="h-7 w-7 text-muted-foreground" onclick={openEditSheet} aria-label="Edit loan interest">
            <PencilIcon class="w-4 h-4" />
          </Button>
        </CardAction>
      </CardHeader>
      <Separator />

      <!-- Desktop: inline editable input -->
      <div class="hidden sm:block px-6 py-4">
        <div class="flex flex-col gap-1.5">
          <Label for="interest-inline">Total Interest Paid (RM)</Label>
          <div class="relative">
            <span class="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">RM</span>
            <Input
              id="interest-inline"
              type="number"
              min="0"
              step="0.01"
              bind:value={localInterestRm}
              class="h-10 pl-10"
              disabled={savingInterest}
              onblur={handleInterestBlur}
            />
          </div>
          {#if savingInterest}
            <p class="text-xs text-muted-foreground m-0">Saving…</p>
          {/if}
        </div>
      </div>

      <!-- Mobile: value display -->
      <CardContent class="sm:hidden px-4 py-4 flex flex-col gap-1">
        <p class="text-xs text-muted-foreground m-0">Total Interest Paid (RM)</p>
        <p class="text-2xl sm:text-[28px] font-semibold text-foreground m-0">{formatRM(property.loan_interest_cents)}</p>
      </CardContent>
    </Card>

    {#if errors.loan_interest_cents}
      <Alert variant="destructive">
        <AlertDescription>{errors.loan_interest_cents[0]}</AlertDescription>
      </Alert>
    {/if}

  {#snippet footer()}
    <Button variant="outline" onclick={() => router.visit(`/properties/${property.id}/rooms`)}>Back</Button>
    <div class="flex-1"></div>
    <Button onclick={() => router.visit(`/properties/${property.id}/statutory_taxes`)}>Next: Taxes &amp; Management →</Button>
  {/snippet}
</PageContainer>

<!-- Edit Loan Interest sheet (mobile only) -->
<Sheet bind:open={editSheetOpen}>
  <SheetContent side="bottom" class="p-0 rounded-t-2xl" showCloseButton={false}>
    <!-- Drag handle -->
    <div class="flex justify-center pt-2">
      <div class="w-9 h-1 rounded-full bg-muted-foreground opacity-40"></div>
    </div>

    <SheetHeader class="flex-row items-center justify-between px-4 py-3 border-b border-border gap-0.5">
      <SheetTitle>Edit Loan Interest</SheetTitle>
      <SheetDescription class="sr-only">Enter the total annual loan interest paid on this property.</SheetDescription>
      <Button variant="ghost" size="icon" class="h-6 w-6 text-muted-foreground -mr-1" onclick={() => { editSheetOpen = false }} aria-label="Close">
        <XIcon class="w-4 h-4" />
      </Button>
    </SheetHeader>

    <div class="px-4 py-5 flex flex-col gap-3">
      <div class="flex flex-col gap-1.5">
        <Label for="interest-input">Total Interest Paid (RM)</Label>
        <div class="relative">
          <span class="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">RM</span>
          <Input
            id="interest-input"
            type="number"
            min="0"
            step="0.01"
            bind:value={interestRm}
            class="h-10 pl-10"
          />
        </div>
        <p class="text-xs text-muted-foreground m-0">Enter the total annual interest amount from your bank loan statement.</p>
      </div>
    </div>

    <SheetFooter class="flex-row px-4 pb-6 pt-0 gap-3 border-t border-border">
      <Button variant="outline" onclick={() => { editSheetOpen = false }} class="flex-shrink-0">Cancel</Button>
      <Button onclick={handleSave} disabled={submitting} class="flex-1">
        {submitting ? 'Saving…' : 'Save'}
      </Button>
    </SheetFooter>
  </SheetContent>
</Sheet>
